import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
} from "@mui/material";
import ShoppingBasket from "@mui/icons-material/ShoppingBasket";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import HouseIcon from "@mui/icons-material/House";
import CloseIcon from "@mui/icons-material/Close";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import Bill from "@mui/icons-material/ReceiptLong";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import logo_cl from "./Images/logo.png";
import { colors } from "./Colors";

const pages = [
  {
    label: "Accueil",
    icon: <HouseIcon fontSize="small" />,
    path: "/dashboard",
  },
  {
    label: "Main courante",
    icon: <MenuBookIcon fontSize="small" />,
    path: "/main-courante",
  },
  {
    label: "Vivre cru",
    icon: <MenuBookIcon fontSize="small" />,
    path: "/vivre-cru",
  },
  {
    label: "Prestations",
    icon: <MenuBookIcon fontSize="small" />,
    path: "/prestations",
  },
  {
    label: "Note de frais",
    icon: <Bill fontSize="small" />,
    path: "/notes-de-frais",
  },
  // Removed: Commander, Commandes, Production
  {
    label: "Comptes",
    icon: <ManageAccountsIcon fontSize="small" />,
    path: "/comptes",
  },
  {
    label: "Tableau de bord",
    icon: <MenuBookIcon fontSize="small" />,
    path: "/tableau-de-bord",
  },
  {
    label: "Articles",
    icon: <ShoppingBasket fontSize="small" />,
    path: "/articles",
  },
  {
    label: "Établissements",
    icon: <HouseIcon fontSize="small" />,
    path: "/etablissements",
  },
  { label: "Sites", icon: <HouseIcon fontSize="small" />, path: "/sites" },
  {
    label: "Employés",
    icon: <ManageAccountsIcon fontSize="small" />,
    path: "/employes",
  },
  {
    label: "Frais Généraux",
    icon: <ManageAccountsIcon fontSize="small" />,
    path: "/fraisGeneraux",
  },
  {
    label: "Budget",
    icon: <ManageAccountsIcon fontSize="small" />,
    path: "/budget",
  },
];

const pagesRole = {
  Gouvernante: ["Accueil"],
  Personnel: ["Accueil"],
  Chef: [
    "Accueil",
    "Main courante",
    "Vivre cru",
    "Note de frais",
    "Articles",
    "Prestations",
  ],
  RS: ["Accueil", "Note de frais", "Établissements", "Sites", "Employés"],
  Admin: [
    "Accueil",
    "Comptes",
    "Note de frais",
    "Frais Généraux",
    "Budget",
    "Prestations",
    "Main courante",
    "Vivre cru",
  ],
};

const Navbar = ({ userRole, setUserRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const allowedPages = pagesRole[userRole]
    ? pages.filter((p) => pagesRole[userRole].includes(p.label))
    : [];

  const handleNavigate = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    localStorage.removeItem("role");
    localStorage.removeItem("token");
    localStorage.removeItem("site");
    setUserRole(null);
    window.location.reload();
    navigate("/login");
  };

  const isActive = (path) =>
    path === "/comptes"
      ? location.pathname.startsWith("/comptes")
      : location.pathname === path;

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1300,
          width: "98%",
          maxWidth: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            bgcolor: "rgba(71, 101, 127, 0.85)",
            backdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: "9999px",
            px: 2.5,
            py: 1,
            boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          }}
        >
          <Box
            onClick={() => handleNavigate("/dashboard")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
            }}
          >
            <Box
              component="img"
              src={logo_cl}
              alt="logo"
              sx={{ height: 32, objectFit: "contain" }}
            />
          </Box>

          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {allowedPages.map((page) => (
                <Button
                  key={page.path}
                  onClick={() => handleNavigate(page.path)}
                  startIcon={page.icon}
                  sx={{
                    color: isActive(page.path)
                      ? "#fff"
                      : "rgba(255,255,255,0.7)",
                    bgcolor: isActive(page.path)
                      ? colors.secondary
                      : "transparent",
                    borderRadius: "9999px",
                    px: 2,
                    py: 0.5,
                    textTransform: "none",
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    "&:hover": {
                      bgcolor: isActive(page.path)
                        ? colors.secondary
                        : "rgba(255,255,255,0.1)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {page.label}
                </Button>
              ))}
            </Box>
          )}

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!isMobile && (
              <>
                <Tooltip title="Mon compte">
                  <IconButton
                    onClick={() => handleNavigate("/moncompte")}
                    sx={{ p: 0.5 }}
                  >
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        border: "2px solid rgba(255,255,255,0.3)",
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Déconnexion">
                  <IconButton
                    onClick={() => setLogoutDialogOpen(true)}
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      "&:hover": { color: "#fff" },
                    }}
                  >
                    <LogoutIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {isMobile && (
              <IconButton
                onClick={() => setMobileOpen(!mobileOpen)}
                sx={{ color: "#fff" }}
              >
                {mobileOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
            )}
          </Box>
        </Box>

        {isMobile && mobileOpen && (
          <Box
            sx={{
              mt: 1,
              bgcolor: "rgba(71, 101, 127, 0.95)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.15)",
              borderRadius: 4,
              px: 2,
              py: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            {allowedPages.map((page) => (
              <Button
                key={page.path}
                onClick={() => handleNavigate(page.path)}
                startIcon={page.icon}
                fullWidth
                sx={{
                  color: isActive(page.path) ? "#fff" : "rgba(255,255,255,0.7)",
                  bgcolor: isActive(page.path)
                    ? colors.secondary
                    : "transparent",
                  borderRadius: 3,
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontSize: "0.9rem",
                  py: 1,
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                {page.label}
              </Button>
            ))}
            <Button
              onClick={() => setLogoutDialogOpen(true)}
              startIcon={<LogoutIcon fontSize="small" />}
              fullWidth
              sx={{
                color: "rgba(255,255,255,0.7)",
                borderRadius: 3,
                justifyContent: "flex-start",
                textTransform: "none",
                fontSize: "0.9rem",
                py: 1,
                mt: 1,
                "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              Déconnexion
            </Button>
          </Box>
        )}
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            bgcolor: colors.primary,
            color: "#fff",
            fontWeight: 700,
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <LogoutIcon fontSize="small" />
          Voulez-vous vraiment vous déconnecter ?
        </DialogTitle>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setLogoutDialogOpen(false)}
            sx={{
              borderRadius: "9999px",
              textTransform: "none",
              fontWeight: 600,
              color: colors.primary,
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleLogout}
            variant="contained"
            sx={{
              borderRadius: "9999px",
              textTransform: "none",
              fontWeight: 700,
              bgcolor: colors.secondary,
              "&:hover": { bgcolor: colors.hover },
            }}
          >
            Se déconnecter
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;
